import { useCallback, useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { createUseStyles } from "$utils/createUseStyles";
import dateformat from "dateformat";
import { theme } from "$utils/theme/bataillog";

import useAuth from "$hooks/useAuth";
import useShippingRecordHistory from "$hooks/useShippingRecordHistory";
import useJStyles from "$hooks/useJStyles";

import { Text, Button, Popin } from "$components";
import JTable from "$components/JTable/JTable";

import { ShippingRecord, ShippingRecordHistory } from "$types";
import {
  formatHistoryFieldValue,
  getShippingRecordHistoryFieldLabel,
} from "$utils/typeConstants";
import { DEFAULT_DATE_FORMAT, DEFAULT_HOUR_FORMAT } from "$utils/utils";
import { CompanyServices } from "$services";
import { queryClient } from "$utils/queryClient";
import { CacheInvalidationAction } from "$utils/cache";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  shippingRecord: ShippingRecord;
};

function ShippingRecordHistoryPopin({
  isOpen,
  onClose,
  shippingRecord,
}: Props) {
  const { userRole } = useAuth();
  const { histories, fetchHistories } = useShippingRecordHistory(
    shippingRecord?.id
  );
  const [companies, setCompanies] = useState([]);
  const { jclasses } = useJStyles();
  const classes = useStyles();

  useEffect(() => {
    if (isOpen) {
      fetchHistories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const fecthClientCompanies = useCallback(async () => {
    const filters = {
      page: 1,
      rowsPerPage: 50000,
    };
    let companies;
    companies = await queryClient.getQueryData([
      CacheInvalidationAction.GET_COMPANIES,
      filters,
    ]);
    if (!companies) {
      companies = await queryClient.fetchQuery({
        queryKey: [CacheInvalidationAction.GET_COMPANIES, filters],
        queryFn: () => CompanyServices.getCompanies(filters),
        staleTime: 60 * 1000,
      });
      companies = companies.data;
      await queryClient.setQueryData(
        [CacheInvalidationAction.GET_COMPANIES, filters],
        companies
      );
    }
    setCompanies(companies);
  }, [setCompanies]);

  useEffect(() => {
    fecthClientCompanies();
  }, [fecthClientCompanies]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const columns = useMemo(
    () => [
      {
        Header: "Date",
        minWidth: 90,
        maxWidth: 90,
        Cell: ({
          row: { original: history },
        }: {
          row: { original: ShippingRecordHistory };
        }) => {
          return (
            <div>
              <Text>{dateformat(history.createdAt, DEFAULT_DATE_FORMAT)}</Text>
              <Text type="disabled">
                {`à ${dateformat(history.createdAt, DEFAULT_HOUR_FORMAT)}`}
              </Text>
            </div>
          );
        },
      },
      {
        Header: "Champ",
        minWidth: 150,
        maxWidth: 150,
        Cell: ({
          row: { original: history },
        }: {
          row: { original: ShippingRecordHistory };
        }) => {
          return (
            <Text>{getShippingRecordHistoryFieldLabel(history.field)}</Text>
          );
        },
      },
      {
        Header: "Ancienne donnée",
        minWidth: 200,
        maxWidth: 200,
        Cell: ({
          row: { original: history },
        }: {
          row: { original: ShippingRecordHistory };
        }) => {
          return (
            <Text>
              {formatHistoryFieldValue(
                history.field,
                history.oldValue,
                companies,
                userRole,
                true
              )}
            </Text>
          );
        },
      },
      {
        Header: "Nouvelle donnée",
        minWidth: 200,
        maxWidth: 200,
        Cell: ({
          row: { original: history },
        }: {
          row: { original: ShippingRecordHistory };
        }) => {
          return (
            <Text>
              {formatHistoryFieldValue(
                history.field,
                history.newValue,
                companies,
                userRole,
                false
              )}
            </Text>
          );
        },
      },
      {
        Header: "Auteur",
        minWidth: 150,
        maxWidth: 150,
        Cell: ({
          row: { original: history },
        }: {
          row: { original: ShippingRecordHistory };
        }) => {
          return <Text>{history.authorName}</Text>;
        },
      },
      {
        Header: "Actions",
        minWidth: 10,
        maxWidth: 10,
        Cell: () => null,
      },
    ],
    [companies, userRole]
  );

  return (
    <Popin
      openPopin={isOpen}
      toggleDrawer={handleClose}
      containerClassName={classes.popinContainer}
    >
      <div className={clsx(jclasses.jWrapper, classes.wrapper)}>
        <Text type="h2">Historique</Text>
        <div className={classes.lineContainer}>
          <Text type="disabled">
            {`Dernière mise à jour le : ${dateformat(
              histories && histories[0]?.createdAt,
              DEFAULT_DATE_FORMAT
            )} `}
          </Text>
        </div>
        <div className={classes.tableContainer}>
          <JTable
            data={histories || []}
            columns={columns}
            hideFilters
            setFilter={() => {}}
            setPageIndex={() => {}}
            hideCheckboxes
            pageCount={1}
            pageIndex={0}
          />
        </div>
        <div className={classes.buttonsContainer}>
          <Button color="secondary" onClick={handleClose}>
            Retour
          </Button>
        </div>
      </div>
    </Popin>
  );
}

export default ShippingRecordHistoryPopin;

const useStyles = createUseStyles(() => ({
  wrapper: {
    display: "flex",
    flex: 1,
    flexDirection: "column",
  },
  popinContainer: {
    display: "flex",
    flex: 1,
  },
  inputContainer: {
    display: "flex",
    flex: 1,
    alignItems: "center",
  },
  lineContainer: {
    display: "flex",
    padding: theme.spacing(2),
  },
  buttonsContainer: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: theme.spacing(4),
  },
  tableContainer: {
    maxHeight: 600,
    overflow: "auto",
  },
}));
